import { assessmentTx } from '../i18n/assessmentTx';
import {
  OTHER_CHOICE_VALUE,
  type AssessmentQuestion,
  type RepeatingListColumn,
} from '../types/assessmentQuestion';

// A SurveyJS element as returned in `DynamicUiPayload.elements`.
// Typed loosely — only the fields we actually consume are required.
// Choice values are numbers on scale questions (PHQ/GAD send 0–3) and strings
// elsewhere ('yes', 'type_1', …).
type SurveyJsChoice =
  | string
  | { value?: string | number; text?: string; imageLink?: string; exclusive?: boolean };

// A `matrixdynamic` column, authored in the survey builder as
// `{ name, title, cellType }`. `cellType` is intentionally not read here — v1
// renders every column as a plain text field regardless (see
// `RepeatingListColumn`'s doc comment).
//
// SurveyJS also accepts a string shorthand — `columns: ["Medication", "Dosage"]`
// — where the string serves as both `name` and `title`, same as `SurveyJsChoice`
// above. Without this arm, a shorthand-authored column's `name` reads as
// `undefined` (a string has no `.name`), `isNamedColumn` drops it, and a
// matrixdynamic authored entirely with shorthand columns ends up with an empty
// `columns` array — the same "no fields to fill" dead-end `AssessmentContent`
// guards against for `columns: []` directly.
type SurveyJsColumn = string | { name?: string; title?: string };

type SurveyJsElement = {
  type: string;
  name: string;
  title?: string;
  /** SurveyJS helper text under the title (the legacy screeners' `additional_info`). */
  description?: string;
  /** Present on `text` elements: `"date"`, `"email"`, etc. */
  inputType?: string;
  /** Inclusive ISO `YYYY-MM-DD` floor on date elements, when authored. Numeric on number inputs. */
  min?: string | number;
  /** Inclusive ISO `YYYY-MM-DD` ceiling on date elements, when authored. Numeric on number inputs. */
  max?: string | number;
  choices?: SurveyJsChoice[];
  /** SurveyJS required flag. Explicitly false → the question is skippable. */
  isRequired?: boolean;
  /** Present on `matrixdynamic` elements. */
  columns?: SurveyJsColumn[];
  /** Present on `matrixdynamic` elements: the builder's authored starting row count. */
  rowCount?: number;
  /** Inline Other free-text on a checkbox (SurveyJS showOtherItem). */
  showOtherItem?: boolean;
  otherText?: string;
  otherPlaceholder?: string;
  isOtherRequired?: boolean;
};

function isNamedColumn(c: SurveyJsColumn): c is string | { name: string; title?: string } {
  return typeof c === 'string' ? c.length > 0 : typeof c.name === 'string' && c.name.length > 0;
}

function toRepeatingListColumns(columns: SurveyJsColumn[] | undefined): RepeatingListColumn[] {
  return (columns ?? [])
    .filter(isNamedColumn)
    .map((c) =>
      typeof c === 'string' ? { name: c, title: c } : { name: c.name, title: c.title ?? c.name }
    );
}

// The builder's own row-count input only accepts whole numbers, so a
// fractional `rowCount` only reaches here through a hand-edited flow JSON
// (like the ones this module's fixtures/tests author directly). Left
// unrounded, it would silently truncate wherever it lands — spread into an
// array literal, or `Array.from({ length })` in `AssessmentContent` — rather
// than failing loudly, and `Infinity` would hang/crash that same call instead
// of falling back. Rounds a finite positive value to honor
// `AssessmentQuestion.initialRowCount`'s "guaranteed positive integer"
// contract; anything else (absent, non-numeric, non-finite, non-positive)
// falls back to 1, same as before.
//
// The outer `Math.max(1, …)` matters on its own: `rowCount` values below 0.5
// (e.g. 0.4) pass the `> 0` guard above but `Math.round` alone would still
// return 0, silently breaking the same "positive integer" contract this
// function exists to uphold — `AssessmentContent` trusts it and doesn't
// re-clamp, so a 0 would reach `Array.from({ length: 0 })` and render a
// required question with no fields and no way to know why.
function toInitialRowCount(rowCount: number | undefined): number {
  if (typeof rowCount !== 'number' || !Number.isFinite(rowCount) || rowCount <= 0) return 1;
  return Math.max(1, Math.round(rowCount));
}

function isSurveyJsElement(v: unknown): v is SurveyJsElement {
  return typeof v === 'object' && v !== null && typeof (v as SurveyJsElement).name === 'string';
}

/**
 * Stringify a choice value, preserving `0` (`String(0)` → `'0'`) — a plain
 * truthiness or `??` chain would drop it. `'0'` is a truthy string, so it also
 * survives `useAssessmentQuestions`' `surveyChoiceUrn ? urn : order` fallback,
 * which previously replaced value 0 with its order and both corrupted PHQ/GAD
 * scores and produced duplicate React keys against numeric value 1.
 * Stringifying is safe server-side: numeric answers are only consumed through
 * `int(value)` coercion (accumulators, phq_gad_scoring, legacy_sync), and
 * equality predicates only compare string answer values.
 */
function toValueString(v: string | number | undefined): string | undefined {
  return v === undefined ? undefined : String(v);
}

function resolveChoiceText(c: SurveyJsChoice): string {
  if (typeof c === 'string') return c;
  return c.text ?? toValueString(c.value) ?? '';
}

function resolveChoiceValue(c: SurveyJsChoice): string {
  if (typeof c === 'string') return c;
  return toValueString(c.value) ?? c.text ?? '';
}

function toOtherSignalKey(elementName: string): string {
  return `${elementName}_other`;
}

function mapChoices(choices: SurveyJsChoice[]) {
  return choices.map((c, choiceIndex) => ({
    text: resolveChoiceText(c),
    order: choiceIndex + 1,
    surveyChoiceUrn: resolveChoiceValue(c),
    imageLink: typeof c === 'string' ? undefined : c.imageLink,
    exclusive: typeof c === 'string' ? undefined : c.exclusive,
  }));
}

/**
 * Converts the raw SurveyJS `elements` array from a `DynamicUiPayload` into the
 * `AssessmentQuestion[]` shape that `AssessmentContent` / `useAssessmentQuestions` consume.
 *
 * Key mapping:
 *   element.name          → question.key   (the signal key, e.g. `taking_aoms`)
 *   element.title         → question.text  (shown as the page heading)
 *   element.description   → question.additionalInfo (the heading subtitle — the
 *                           legacy screeners' `additional_info`: lab_preference's
 *                           in-network cost note, the AOM refill "best estimate"
 *                           reassurance, every pre-diabetes question's explainer)
 *   choice.value          → surveyChoiceUrn (used by useAssessmentQuestions as the
 *                           submitted answer value — no URN needed here;
 *                           numeric values are stringified, see toValueString)
 *   choice.imageLink      → imageLink       (optional per-choice icon URL, e.g.
 *                           health goals; rendered as a leading avatar when set)
 *   choice.exclusive      → exclusive       (mutually-exclusive checkbox choice,
 *                           e.g. health_conditions' "None of these" — selecting
 *                           it clears every other choice and vice versa; only
 *                           meaningful on multi-select questions)
 *   type: "checkbox"      → isMultiSelect: true (AssessmentContent renders checkbox
 *                           rows + submits a string[]); radiogroup → single-select
 *   showOtherItem         → showOtherItem / otherKey / optional otherText,
 *                           otherPlaceholder, isOtherRequired (checkbox only;
 *                           injects a value "other" choice when missing)
 *   inputType: "date"     → questionType: "date" (AssessmentContent renders date input)
 *   min / max             → question.minDate / question.maxDate (ISO date
 *                           bounds; copied only for date questions, and only
 *                           when they are non-empty strings)
 *   type: "text"          → questionType: "text" (single-line free-text input),
 *                           unless inputType is "date" (checked first, above)
 *   inputType: "number"   → question.inputType: "number" (AssessmentContent
 *                           switches the on-screen keyboard to numeric, e.g.
 *                           goal weight / highest adult weight)
 *   type: "comment"       → questionType: "comment" (multiline free-text input)
 *   type: "matrixdynamic" → questionType: "repeatingList" (AssessmentContent
 *                           renders open-ended repeating rows with add/remove);
 *                           columns  → question.columns (name/title pairs,
 *                           carried through so the renderer knows which
 *                           fields to show per row — see RepeatingListColumn);
 *                           rowCount → question.initialRowCount (the
 *                           renderer's STARTING row count, not a cap)
 *   isRequired            → SurveyJS omits the flag when a question is
 *                           optional, so an absent flag resolves to `false`
 *                           here for every element type — the opposite of
 *                           the legacy default (see AssessmentQuestion.isRequired).
 *                           Every live production flow sets the flag
 *                           explicitly today, so this is currently
 *                           unreachable in practice, but it's the correct
 *                           SurveyJS-wide reading if a future flow omits it.
 */
export function toDynamicAssessmentQuestions(
  elements: unknown[],
  /** Page-level title fallback when the element has no own title (e.g. refill date). */
  pageTitle?: string | null,
  /** Page-level description fallback when the element has no own description. */
  pageDescription?: string | null
): AssessmentQuestion[] {
  return elements.filter(isSurveyJsElement).map((element, index) => {
    const questionType =
      element.type === 'matrixdynamic'
        ? 'repeatingList'
        : element.inputType === 'date'
          ? 'date'
          : element.type === 'text'
            ? 'text'
            : element.type === 'comment'
              ? 'comment'
              : 'choice';
    const isRepeatingList = questionType === 'repeatingList';
    const isDate = questionType === 'date';
    const min = isDate && typeof element.min === 'string' && element.min ? element.min : undefined;
    const max = isDate && typeof element.max === 'string' && element.max ? element.max : undefined;
    const showOtherItem = element.type === 'checkbox' && element.showOtherItem === true;
    let choices = mapChoices(element.choices ?? []);
    if (showOtherItem && !choices.some((c) => c.surveyChoiceUrn === OTHER_CHOICE_VALUE)) {
      choices = [
        ...choices,
        {
          text: element.otherText ?? assessmentTx.t('Other'),
          order: choices.length + 1,
          surveyChoiceUrn: OTHER_CHOICE_VALUE,
          imageLink: undefined,
          exclusive: undefined,
        },
      ];
    }

    return {
      key: element.name,
      order: index + 1,
      text: element.title ?? pageTitle ?? '',
      // Rendered as the ScreenHeading subtitle. `undefined` (not '') when neither
      // level carries one, so AssessmentContent omits the subtitle node entirely.
      additionalInfo: element.description ?? pageDescription ?? undefined,
      questionType,
      // Only relevant when questionType is 'text' (e.g. "number" for goal
      // weight / highest adult weight); AssessmentContent reads it to switch
      // the keyboard. Omitted for 'date', which already has its own input.
      inputType: questionType === 'text' ? element.inputType : undefined,
      ...(min ? { minDate: min } : {}),
      ...(max ? { maxDate: max } : {}),
      // SurveyJS omits `isRequired` to mean optional, so an absent flag
      // resolves to `false` here for every element type — not just
      // text/comment. This differs from the legacy pass-through default
      // (absent → required), but that default belongs to a different
      // producer (`useAssignedScreener`, which never reaches this
      // converter): legacy screeners rely on it because they never send the
      // flag at all, whereas every SurveyJS flow either sets it explicitly
      // or means optional by omitting it.
      isRequired: element.isRequired === true,
      // SurveyJS `checkbox` → multi-select; `radiogroup` (and everything else) →
      // single-select. Drives checkbox-vs-radio rendering + array-vs-string answer.
      isMultiSelect: element.type === 'checkbox',
      choices,
      ...(showOtherItem
        ? {
            showOtherItem: true,
            otherKey: toOtherSignalKey(element.name),
            ...(element.otherText != null ? { otherText: element.otherText } : {}),
            ...(element.otherPlaceholder ? { otherPlaceholder: element.otherPlaceholder } : {}),
            ...(element.isOtherRequired === true ? { isOtherRequired: true } : {}),
          }
        : {}),
      columns: isRepeatingList ? toRepeatingListColumns(element.columns) : undefined,
      // rowCount is the builder's STARTING count (default 2 there), not a cap —
      // AssessmentContent lets the member add further rows without limit. See
      // toInitialRowCount for the fallback/rounding rules.
      initialRowCount: isRepeatingList ? toInitialRowCount(element.rowCount) : undefined,
    };
  });
}
